import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { InstantCardSearch } from '@sorare/core/src/components/search/InstantSearch';
import { AnalyticTag } from '@sorare/core/src/components/search/InstantSearch/types';
import {
  LeftFilters,
  SearchLayoutContainer,
  SearchLayoutMain,
  VerticalScroll,
} from '@sorare/core/src/components/search/SearchLayout/ui';
import { AlgoliaCardIndexesNames } from '@sorare/core/src/contexts/config';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import SearchCardsContextProvider from '@sorare/core/src/contexts/searchCards/Provider';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import useToggle from '@sorare/core/src/hooks/useToggle';
import { FiltersGroup } from '@sorare/core/src/lib/filters';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PlayerDetails } from 'components/player/PlayerDrawer';
import CurrentMarketplaceUserProvider from 'contexts/currentMarketplaceUser/Provider';
import useDefaultFilters from 'hooks/useDefaultFilters';
import { ActiveFilters } from 'search/ActiveFilters';
import FiltersManager from 'search/FiltersManager';
import FiltersManagerDialog from 'search/FiltersManagerDialog';
import SearchBox from 'search/SearchBox';
import ToggleFiltersManager from 'search/ToggleFiltersManager';
import { messages } from 'searchCards/AdvancedCardSearch/SearchLayout';
import CardResultsFromGraphQL from 'searchCards/CardResultsFromGraphQL';
import { CollectionNavigationSwitch } from 'searchCards/CollectionNavigationSwitch';
import SortCards from 'searchCards/SortCards';
import { TotalResults } from 'searchCards/TotalResults';

const Cards = styled.div`
  position: relative;
  border-radius: 8px;
`;
const SearchCtn = styled(Vertical).attrs({ gap: 2 })`
  padding: 0 0 var(--double-unit);
`;
const SearchRow = styled(Horizontal).attrs({ gap: 1.5 })`
  justify-content: space-between;
`;
const SortAndSegment = styled(Vertical).attrs({ gap: 1.5 })`
  @media ${laptopAndAbove} {
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: flex-start;
  }
`;
const SortCtn = styled(Horizontal)`
  width: 100%;
  padding-bottom: var(--intermediate-unit);
  border-bottom: 1px solid rgba(var(--c-rgb-neutral-1000), 0.11);
  @media ${laptopAndAbove} {
    width: auto;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

type Props = {
  sport: Sport;
  analyticsTags: AnalyticTag[];
  availableSorts: AlgoliaCardIndexesNames;
  cardFilters: FiltersGroup[];
  playerDetailsComponent: PlayerDetails;
  defaultFilters?: Nullable<Partial<Parameters<typeof useDefaultFilters>[0]>>;
};
export const MyCardsSearch = ({
  sport,
  analyticsTags,
  availableSorts,
  cardFilters,
  playerDetailsComponent,
  defaultFilters: defaultFiltersProp,
}: Props) => {
  const { formatMessage } = useIntl();
  const { currentUser } = useCurrentUserContext();
  const { up: isLaptop } = useScreenSize('laptop');
  const includeCommonCards = !currentUser?.userSettings?.hideCommonCards;
  const [filtersOpen, toggleFilters] = useToggle(false);
  const defaultFilters = useDefaultFilters({
    ...defaultFiltersProp,
    userId: idFromObject(currentUser?.id),
  });

  return (
    <CurrentMarketplaceUserProvider sport={sport}>
      <SearchCardsContextProvider
        includeCommonCards={Boolean(includeCommonCards)}
        cardFilters={cardFilters}
        galleryOwnerSlug={currentUser?.slug}
      >
        <InstantCardSearch
          indexes={['Cards New']}
          availableSorts={availableSorts}
          analyticsTags={analyticsTags}
          sport={sport}
          defaultFilters={defaultFilters}
          urlState
        >
          <SearchLayoutContainer>
            {isLaptop && (
              <VerticalScroll>
                <LeftFilters visible>
                  <SearchCtn>
                    <SearchBox
                      withClearIcon
                      placeholder={formatMessage(messages.defaultPlaceholder)}
                    />
                    <SearchRow>
                      <TotalResults />
                    </SearchRow>
                  </SearchCtn>
                  <FiltersManager filters={cardFilters} skipClearAllButton />
                </LeftFilters>
              </VerticalScroll>
            )}
            <SearchLayoutMain>
              <SortAndSegment>
                {isLaptop && (
                  <SortCtn>
                    <SortCards indexes={availableSorts} />{' '}
                  </SortCtn>
                )}
                <SBHorizontal>
                  {!isLaptop && (
                    <div>
                      <ToggleFiltersManager
                        toggleFilters={toggleFilters}
                        color="quaternary"
                      />
                    </div>
                  )}
                  <CollectionNavigationSwitch />
                </SBHorizontal>
              </SortAndSegment>
              <ActiveFilters />
              <Cards>
                <CardResultsFromGraphQL
                  hideOwner
                  galleryOwnerSlug={currentUser?.slug}
                  showDesktopFilter
                  PlayerDetailsComponent={playerDetailsComponent}
                  sport={sport}
                  editableLists
                />
              </Cards>
            </SearchLayoutMain>
          </SearchLayoutContainer>
          {!isLaptop && (
            <FiltersManagerDialog
              open={filtersOpen}
              onClose={toggleFilters}
              filtersGroups={cardFilters}
              sorts={availableSorts}
            >
              <Vertical>
                <SearchBox
                  withClearIcon
                  placeholder={formatMessage(messages.defaultPlaceholder)}
                />
                <SearchRow>
                  <TotalResults />
                </SearchRow>
              </Vertical>
            </FiltersManagerDialog>
          )}
        </InstantCardSearch>
      </SearchCardsContextProvider>
    </CurrentMarketplaceUserProvider>
  );
};
